<script setup lang="ts" name="TimelineChart">
import { computed, ref, useTemplateRef } from 'vue';
import { useResizeObserver } from '@vueuse/core';

import { useTimelineChartData } from '~/features/useTimelineChartData';

import TimelineChartGroups from '~/components/TimelineChartGroups.vue';
import TimelineChartHeader from '~/components/TimelineChartHeader.vue';
import TimelineChartRows from '~/components/TimelineChartRows.vue';

import type { IDataTableAction, ITimelineChartBaseRow } from '~/types';

const { startDate, endDate, bufferDays, rows } = defineProps<{
  startTimestamp?: number;
  endDate?: number;
  startDate?: number;
  bufferDays?: number;
  actions: () => IDataTableAction[];
  rows: ITimelineChartBaseRow[];
}>();

const rowsRef = useTemplateRef('rowsRef');
const groupRef = useTemplateRef('groupRef');
const headerRef = useTemplateRef('headerRef');

const scrollableRefClientWidth = ref(0);
const hoveredRowId = ref<string | number | null>(null);
const hasVerticalScrollbar = ref(false);

const onMouseEnter = (id: string | number) => {
  hoveredRowId.value = id;
};
const onMouseLeave = () => {
  hoveredRowId.value = null;
};

useResizeObserver(rowsRef, () => {
  if (rowsRef.value) {
    scrollableRefClientWidth.value = rowsRef.value.clientWidth;
    hasVerticalScrollbar.value =
      rowsRef.value.scrollHeight > rowsRef.value.clientHeight;
  }
});

const syncScroll = (e: Event) => {
  const target = e.target as HTMLElement;
  const { scrollLeft, scrollTop } = target;

  if (rowsRef.value === target) {
    headerRef.value!.scrollLeft = scrollLeft;
    groupRef.value!.scrollTop = scrollTop;
  }

  if (groupRef.value === target) {
    rowsRef.value!.scrollTop = scrollTop;
  }

  if (headerRef.value === target) {
    rowsRef.value!.scrollLeft = scrollLeft;
  }
};

const {
  timelineRows,
  monthDates,
  totalColumns,
  todayColumnIndex,
  columnWidth,
  toggleRowOpen,
  scrollWidth,
  isOverflowing,
} = useTimelineChartData({
  startTs: computed(() => startDate),
  endTs: computed(() => endDate),
  bufferDays: computed(() => bufferDays ?? 3),
  rows: computed(() => rows),
  scrollableWidth: scrollableRefClientWidth,
});
</script>

<template>
  <div
    class="grid grid-cols-[max-content,_minmax(0,1fr)] overflow-hidden rounded-md shadow"
  >
    <div :class="{ 'cargo-booking-sticky-shadow z-10': isOverflowing }">
      <div class="h-[96px] bg-charcoal-2"></div>
      <div
        class="scroll-hidden relative max-h-[720px] overflow-y-auto"
        ref="groupRef"
        @scroll.passive="syncScroll"
      >
        <TimelineChartGroups
          :hoveredRowId="hoveredRowId"
          :timelineRows="timelineRows"
          @mouseEnter="onMouseEnter"
          @mouseLeave="onMouseLeave"
          @open="toggleRowOpen"
          @close="toggleRowOpen"
        />
        <div class="h-full bg-white" v-if="isOverflowing">
          <div class="invisible w-0 overflow-y-hidden overflow-x-scroll"></div>
        </div>
      </div>
    </div>
    <div class="relative isolate border-r border-charcoal-2 bg-white">
      <div class="flex">
        <div
          class="scroll-hidden flex overflow-x-auto"
          ref="headerRef"
          v-drag-scroll
          @scroll.passive="syncScroll"
        >
          <TimelineChartHeader
            :monthDates="monthDates"
            :columnWidth="columnWidth"
          />
          <div
            class="min-h-full"
            :class="
              (monthDates.length - 1) % 2 === 0
                ? 'bg-charcoal-2'
                : 'bg-charcoal-3'
            "
            v-if="hasVerticalScrollbar"
          >
            <div
              class="invisible h-0 overflow-x-hidden overflow-y-scroll"
            ></div>
          </div>
        </div>
      </div>
      <div
        class="max-h-[720px] overflow-x-auto overflow-y-auto"
        ref="rowsRef"
        v-drag-scroll
        @scroll.passive="syncScroll"
      >
        <TimelineChartRows
          :totalColumns="totalColumns"
          :todayColumnIndex="todayColumnIndex"
          :timelineRows="timelineRows"
          :columnWidth="columnWidth"
          :scrollWidth="scrollWidth"
          :hoveredRowId="hoveredRowId"
          :actions="actions"
          @mouseEnter="onMouseEnter"
          @mouseLeave="onMouseLeave"
        />
      </div>
    </div>
  </div>
</template>

<style>
.scroll-hidden {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.scroll-hidden::-webkit-scrollbar {
  display: none;
}
</style>
